const { Task } = require("./task.js");


class Project {
    constructor(simulator, name, currProjectData, startOffset, projectIndex) {
        this.simulator = simulator;
        this.name = name;
        this.startHour = startOffset;
        this.delay = 0;

        this.parseProjectDataList(currProjectData, startOffset);

        this.initialTasks = this._getInitialTasks();
        this.connectAllTasks();
        this.finalTasks = this._getFinalTasks();
        this.maxRowAmount = this._getMaxRowAmount();
        this.projectIndex = projectIndex;
        
        
        this.originalStartHour = 0;
        
        
        this.shiftAllTasks(startOffset);
        this.longestChainLength = this.getMinimumEndHour() - this.startHour;
    }

    getData(){
        return {
            isFinished: this.isFinished(),
            dueDate: this.getDueDate(),
            minimumEnd: this.getMinimumEndHour(),
            currTasks: this.taskArray.map(task => task.getData())
        };
    }

    isActive(){
        return this.startHour <= this.simulator.hoursPassed && !this.isFinished();
    }

    updateStartHour(startHour, updateOriginalStartHour){
        this.startHour = startHour;
        this.initialTasks.forEach((currTask) => {
            currTask.startHour = startHour;
        });

        this.shiftAllTasks(this.simulator.hoursPassed);
        
        if(!updateOriginalStartHour){
            return;
        }

        //Iterate through the tasks, and update the original start hour
        this.originalStartHour = startHour;
        this.taskArray.forEach((currTask) => {
            currTask.originalStartHour = currTask.startHour;
        });
        this.simulator.performanceTracker.applyForceUpdateMetrics();


    }

    //Internal function which gets the max amount of vertical tasks in a project
    _getMaxRowAmount() {
        var maxAmount = 0;

        this.taskArray.forEach((currTask) => {
            maxAmount = Math.max(maxAmount, currTask.row);
        });

        return maxAmount + 1;
    }

    getRemainingTime(){
        var remainingTime = this.getDueDate() - this.startHour;

        return remainingTime;
    }

    getBufferRatio(){
        var remainingTime = this.getRemainingTime();
        var soonestEndTime = this.getMinimumEndHour();
        var remainingWork = soonestEndTime - this.startHour;
        var ratio = remainingWork / remainingTime;

        return ratio;
    }

    getProjectStatus(){
        var remainingTime = this.getRemainingTime();
        var ratio = this.getBufferRatio();

        //The project is not finished, but we don't have any time left
        if (remainingTime <= 0 && !this.isFinished()) {
            return "red";
        }

        //The project is finished, but we don't have any time left
        if (remainingTime <= 0 && this.isFinished()) {
            return "green";
        }

        //Enough time and buffer
        if (ratio < 0.6) {
            return "blue";
        }

        //Enough time and buffer
        if (ratio >= 0.6 && ratio < 0.8) {
            return "green";
        }

        //Buffer getting short
        if (ratio >= 0.8 && ratio <= 1) {
            return "yellow";
        }

        //Too late
        if (ratio >= 1) {
            return "red";
        }
    }

    //Calculate the Estimated Profit
    getEstimatedProfit() {
        var totalHours = this.getTotalHours();
        var totalRevenue = this.revenue;

        return totalRevenue - this.simulator.params.costPerLabourHour * totalHours;

    }

    //Get the total amount of hours for all the tasks
    getTotalHours(colorCode) {
        var totalHours = 0;

        this.taskArray.forEach((currTask) => {
            if(colorCode == null || currTask.resourceGroupID == colorCode){
                totalHours += currTask.totalWorkHours;
            }
        });

        return totalHours;
    }
    

    //Parses the given project data
    parseProjectDataList(currProjectData, startOffset) {
        this.taskArray = [];

        currProjectData.forEach(currDataPoint => {
            if (currDataPoint["Task_Type"] == "Task") {
                var currTask = new Task(this, currDataPoint, startOffset);
                this.taskArray.push(currTask);
            }
            else if (currDataPoint["Task_Type"] == "Project") {
                this.leadTimeHours = parseInt(currDataPoint["Lead_time_hour"]);
                this.revenue = parseInt(currDataPoint["Turnover project"]);
                this.constrainedThroughput = parseInt(currDataPoint["T_Per_CCR_Hour"]);
            }
        })

        //For ease of use later also have a dictionary available
        this.taskDict = {};
        this.taskArray.forEach((currTask) => {
            this.taskDict[currTask.taskID] = currTask;
        });
    }

    //Internal function to get the final tasks
    _getFinalTasks() {
        var returnArray = [];

        this.taskArray.forEach((currTask) => {
            if (currTask.isFinalTask()) returnArray.push(currTask);
        });

        return returnArray;
    }

    //Internal function to get the initial tasks
    _getInitialTasks() {
        var returnArray = [];

        this.taskArray.forEach((currTask) => {
            if (currTask.isInitialTask()) returnArray.push(currTask);
        });

        return returnArray;
    }

    //Get the minimum start hour of the initial tasks
    getMinimumStartHour() {
        var beginHours = [];

        this.initialTasks.forEach((initialTask) => {
            beginHours.push(initialTask.startHour);
        });

        if (beginHours.length == 0) {
            return -1;
        }

        return Math.min(...beginHours);
    }

    //Get the minimumEndhour of the final tasks (the soonest possible time)
    getMinimumEndHour() {
        var endHours = [];

        this.finalTasks.forEach((finalTask) => {
            endHours.push(finalTask.startHour + finalTask.totalWorkHours);
        });

        if (endHours.length == 0) {
            return -1;
        }

        return Math.max(...endHours);
    }

    isDelayed() {
        if (this.isFinished()) {
            return false;
        }

        var dueDate = this.startHour + this.leadTimeHours;
        var plannedEnd = this.getMinimumEndHour();

        return dueDate <= plannedEnd;
    }

    //Get the milestone due date
    getDueDate() {
        return this.startHour + this.leadTimeHours;
    }

    //Check if the project is finished
    isFinished() {
        var allFinished = true;
        this.finalTasks.forEach((finalTask) => {
            allFinished &= finalTask.isFinished();
        });

        return allFinished;
    }

    //Get all tasks which are ready to be worked on at the current moment
    getReadyToStartTasks() {
        var returnTasks = [];
        this.taskArray.forEach((currTask) => {
            if (currTask.isReadyToStart()) {
                returnTasks.push(currTask);
            }
        });

        return returnTasks;
    }

    projectTotalHours(){
        var totalHours = 0;
        this.taskArray.forEach((currTask) => {
            totalHours += currTask.totalWorkHours;
        });

        return totalHours;
    }

    projectTotalRemainingWork(){
        var remainingHours = 0;
        this.taskArray.forEach((currTask) => {
            if(currTask.isFinished()) return;
            remainingHours += currTask.getRemainingWorkHours();
        });

        return remainingHours;
    }

    //Shift all the tasks, since shiftToLeft is recursive, we only need to do it for the final states
    shiftAllTasks(hoursPassed) {
        //For optimisation purposes
        this.taskArray.forEach((currTask) => {
            currTask.shiftedThisIteration = false;
        });

        this.finalTasks.forEach((finalTask) => {
            finalTask.shiftToLeft(hoursPassed);
        });
    }


    //For each task, connect them to their parents and childs
    connectAllTasks() {
        this.taskArray.forEach((currTask) => {
            currTask.predecessorsIDS.forEach((predID) => {
                if (predID != "") {
                    var parentTask = this.taskDict[predID];

                    currTask.registerParentTask(parentTask);
                    parentTask.registerChildTask(currTask);
                }
            });
        });
    }
}


module.exports = { Project };